import { Stack } from '@mui/material';
import { ConductVisibility } from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import { CONDUCT_STATUS_OPTIONS } from './ConductVisibilitySelectMultiple';
import {
  ConductVisibilityTagSelect,
  ConductVisibilityTagSelectProps,
} from './ConductVisibilityTagSelect';

type ConductVisibilityExpandedSelectProps = PropsWithChildren<{
  selectedVisibility: ConductVisibility[];
  onApply: (v: ConductVisibility[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;

export const ConductVisibilityExpandedSelect: FC<ConductVisibilityExpandedSelectProps> = ({
  selectedVisibility: initialSelectedVisibility,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedVisibility, setSelectedVisibility] =
    useState<ConductVisibility[]>(initialSelectedVisibility);

  const toggleVisibility = useCallback((v: ConductVisibility) => {
    setSelectedVisibility((visibility) => toggleMultipleValueArrayProperty(visibility, [v]));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedVisibility);
    onClose();
  }, [selectedVisibility, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderConductVisibilityTags({
            visibilities: CONDUCT_STATUS_OPTIONS.map((o) => o.value),
            tagProps: (status) => ({
              variant: selectedVisibility.includes(status) ? 'filled' : undefined,
            }),
            onClick: toggleVisibility,
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [handleApply, onClear, toggleVisibility, selectedVisibility]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedVisibility.length > 0}
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
    >
      {renderConductVisibilityTags({
        tagProps: { size: 'small' },
        visibilities: selectedVisibility,
        onDelete: toggleVisibility,
      })}
    </ExpandedSelect>
  );
};

type RenderConductVisibilityTagsParams = {
  visibilities: ConductVisibility[];
  onDelete?: (v: ConductVisibility) => void;
  onClick?: (v: ConductVisibility) => void;
  tagProps?:
    | Omit<ConductVisibilityTagSelectProps, 'visibility'>
    | ((v: ConductVisibility) => Omit<ConductVisibilityTagSelectProps, 'visibility'>);
};

export const renderConductVisibilityTags = ({
  visibilities,
  onDelete,
  onClick,
  tagProps,
}: RenderConductVisibilityTagsParams) =>
  visibilities.map((v) => (
    <ConductVisibilityTagSelect
      key={v}
      visibility={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
